/**
 * ControlsTab -- Data-driven admin controls for the UserDetailPage.
 *
 * Renders ALL templateable fields organized by TEMPLATE_CATEGORIES,
 * using a mosaic grid layout for dense, scannable admin UX.
 * Boolean fields toggle immediately; number/string/enum fields use
 * local edit + Save. Array fields (tags) use a tag-input with Enter-to-add.
 *
 * Each category is a card with an accent left-border stripe and tinted
 * icon header. Boolean toggles render in a tight mosaic grid, while
 * non-boolean controls get full-width rows beneath them.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Icon } from "../../ui/Icon";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { Spinner } from "../../ui/Spinner";
import { Switch } from "../../ui/Switch";
import { TagPicker } from "../../ui/TagPicker";
import { Tooltip } from "../../ui/Tooltip";
import { CCTooltip } from "../../ui/CCTooltip";
import { cn } from "../../../utils/cn";
import { toast } from "../../../lib/toast";
import type { UserProfile } from "../../../hooks/useUserProfile";
import type { UserSettingsPatch } from "../../../hooks/useUserSettings";
import type { UserPolicyData } from "../../../hooks/useUserPolicy";
import { TEMPLATE_CATEGORIES, type FieldCategory, type FieldDef } from "../template-fields";
import { LibraryPickerPopover, normalizeId } from "./LibraryPickerPopover";

// ============================================================================
// Types
// ============================================================================

export type ControlsTabProps = {
  profile: UserProfile;
  policy: UserPolicyData | null;
  policyLoading: boolean;
  settingsUpdating: boolean;
  policyUpdating: boolean;
  onUpdateSettings: (patch: UserSettingsPatch) => Promise<void>;
  onUpdatePolicy: (update: Partial<UserPolicyData>) => Promise<void>;
  onRefreshProfile: () => void;
};

// ============================================================================
// Accent system
// ============================================================================

type AccentColor = FieldCategory["accent"];

const accentMap: Record<AccentColor, { border: string; iconBg: string; iconColor: string }> = {
  amber: { border: "border-l-amber-500", iconBg: "bg-amber-500/10", iconColor: "text-amber-400" },
  sky: { border: "border-l-sky-500", iconBg: "bg-sky-500/10", iconColor: "text-sky-400" },
  violet: { border: "border-l-violet-500", iconBg: "bg-violet-500/10", iconColor: "text-violet-400" },
  rose: { border: "border-l-rose-500", iconBg: "bg-rose-500/10", iconColor: "text-rose-400" },
  slate: { border: "border-l-slate-500", iconBg: "bg-muted/20", iconColor: "text-muted-foreground" },
  emerald: { border: "border-l-emerald-500", iconBg: "bg-emerald-500/10", iconColor: "text-emerald-400" },
  cyan: { border: "border-l-cyan-500", iconBg: "bg-cyan-500/10", iconColor: "text-cyan-400" },
  pink: { border: "border-l-pink-500", iconBg: "bg-pink-500/10", iconColor: "text-pink-400" },
  indigo: { border: "border-l-indigo-500", iconBg: "bg-indigo-500/10", iconColor: "text-indigo-400" },
  teal: { border: "border-l-teal-500", iconBg: "bg-teal-500/10", iconColor: "text-teal-400" },
  orange: { border: "border-l-orange-500", iconBg: "bg-orange-500/10", iconColor: "text-orange-400" },
};

// ============================================================================
// Value resolver: reads current field value from profile
// ============================================================================

function getFieldValue(
  field: FieldDef,
  profile: UserProfile,
  wsdPolicy: UserPolicyData | null,
): unknown {
  if (field.bucket === "wsdLocal") {
    if (field.key === "bypassKillStream") return wsdPolicy?.bypassKillStream ?? false;
    return field.defaultValue;
  }
  if (field.bucket === "config") {
    const cfg = profile.config;
    if (!cfg) return field.defaultValue;
    // Map template key to config interface key (camelCase first char)
    const camelKey = field.key.charAt(0).toLowerCase() + field.key.slice(1);
    const val = (cfg as unknown as Record<string, unknown>)[camelKey];
    return val !== undefined ? val : field.defaultValue;
  }
  // policy bucket — check profile-level fields first, then policy object
  if (field.key === "IsDisabled") return profile.isDisabled;
  if (field.key === "IsHidden") return profile.isHidden;

  // EnabledFolderNames: use backend-resolved names (preferred) or raw IDs (fallback)
  if (field.key === "EnabledFolderNames") {
    const backendNames: string[] = profile.policy.enabledFolderNames ?? [];
    const rawIds: string[] = profile.policy.enabledFolders ?? [];
    // Use backend-resolved names if available; fall back to raw IDs
    return backendNames.length > 0 ? backendNames : rawIds;
  }

  // Map PascalCase upstream field key to camelCase profile.policy key
  const policyKey = field.key.charAt(0).toLowerCase() + field.key.slice(1);
  const val = (profile.policy as unknown as Record<string, unknown>)[policyKey];
  return val !== undefined ? val : field.defaultValue;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Section header with tinted icon badge */
const SectionHeader = ({
  icon,
  accent,
  children,
  count,
}: {
  icon: string;
  accent: AccentColor;
  children: React.ReactNode;
  count?: number;
}) => {
  const a = accentMap[accent];
  return (
    <div className="flex items-center gap-2 mb-1.5">
      <div className={cn("rounded-md flex items-center justify-center h-5 w-5 shrink-0", a.iconBg)}>
        <Icon icon={icon} className={cn("h-3 w-3", a.iconColor)} />
      </div>
      <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground/70">
        {children}
      </h4>
      {count !== undefined && count > 0 && (
        <Badge variant="muted" size="xs">{count}</Badge>
      )}
    </div>
  );
};

/** Boolean toggle tile for mosaic grid */
const ToggleTile = ({
  field,
  value,
  disabled,
  onToggle,
}: {
  field: FieldDef;
  value: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}) => (
  <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md hover:bg-muted/30 transition-colors min-w-0 overflow-hidden">
    <div className="flex items-center gap-2 min-w-0 overflow-hidden">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full shrink-0 transition-colors",
          value ? "bg-emerald-500" : "bg-muted-foreground/25",
        )}
        aria-hidden="true"
      />
      <CCTooltip label={field.label} description={field.description} serverOnly={field.serverOnly}>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs 2xl:text-sm text-foreground truncate block">{field.label}</span>
          {field.serverOnly && (
            <Badge
              variant={field.serverOnly === "emby" ? "success" : "muted"}
              size="xs"
              className="capitalize shrink-0"
            >
              {field.serverOnly}
            </Badge>
          )}
        </div>
        <p className="text-[10px] 2xl:text-xs text-muted-foreground/60 mt-0.5 truncate block max-w-full">
          {field.description}
        </p>
      </CCTooltip>
    </div>
    <Switch
      checked={value}
      onChange={onToggle}
      disabled={disabled}
      aria-label={field.label}
    />
  </div>
);

/** Number input with local edit + Save — grid-friendly layout */
const NumberControl = ({
  field,
  value,
  disabled,
  onSave,
}: {
  field: FieldDef;
  value: number;
  disabled: boolean;
  onSave: (val: number) => void;
}) => {
  const [local, setLocal] = useState(value);
  const isDirty = local !== value;

  useEffect(() => { setLocal(value); }, [value]);

  return (
    <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md hover:bg-muted/30 transition-colors min-w-0 overflow-hidden">
      <CCTooltip label={field.label} description={field.description} serverOnly={field.serverOnly} className="flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs 2xl:text-sm text-foreground truncate block">{field.label}</span>
          {field.serverOnly && (
            <Badge variant={field.serverOnly === "emby" ? "success" : "muted"} size="xs" className="capitalize shrink-0">
              {field.serverOnly}
            </Badge>
          )}
        </div>
        <p className="text-[10px] 2xl:text-xs text-muted-foreground/60 mt-0.5 truncate block max-w-full">
          {field.description}
        </p>
      </CCTooltip>
      <div className="flex items-center gap-1.5 shrink-0">
        <Input
          size="compact"
          type="number"
          min={field.min ?? 0}
          max={field.max}
          value={local}
          onChange={(e) => setLocal(Math.max(field.min ?? 0, parseInt(e.target.value, 10) || 0))}
          disabled={disabled}
          className="w-16 text-right tabular-nums"
          aria-label={field.label}
        />
        {field.unit && (
          <span className="text-[10px] 2xl:text-xs text-muted-foreground whitespace-nowrap">{field.unit}</span>
        )}
        {isDirty && (
          <Button variant="primary" size="sm" className="h-6 px-2 text-xs" onClick={() => onSave(local)} disabled={disabled}>
            Save
          </Button>
        )}
      </div>
    </div>
  );
};

/** String input with local edit + Save — grid-friendly layout */
const StringControl = ({
  field,
  value,
  disabled,
  onSave,
}: {
  field: FieldDef;
  value: string;
  disabled: boolean;
  onSave: (val: string) => void;
}) => {
  const [local, setLocal] = useState(value);
  const isDirty = local !== value;

  useEffect(() => { setLocal(value); }, [value]);

  return (
    <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md hover:bg-muted/30 transition-colors min-w-0 overflow-hidden">
      <CCTooltip label={field.label} description={field.description} serverOnly={field.serverOnly} className="flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs 2xl:text-sm text-foreground truncate block">{field.label}</span>
          {field.serverOnly && (
            <Badge variant={field.serverOnly === "emby" ? "success" : "muted"} size="xs" className="capitalize shrink-0">
              {field.serverOnly}
            </Badge>
          )}
        </div>
        <p className="text-[10px] 2xl:text-xs text-muted-foreground/60 mt-0.5 truncate block max-w-full">
          {field.description}
        </p>
      </CCTooltip>
      <div className="flex items-center gap-1.5 shrink-0">
        <Input
          size="compact"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          disabled={disabled}
          placeholder={field.description}
          className="w-32 text-xs"
          aria-label={field.label}
        />
        {isDirty && (
          <Button variant="primary" size="sm" className="h-6 px-2 text-xs" onClick={() => onSave(local)} disabled={disabled}>
            Save
          </Button>
        )}
      </div>
    </div>
  );
};

/** Enum select with immediate save — grid-friendly layout */
const EnumControl = ({
  field,
  value,
  disabled,
  onSave,
}: {
  field: FieldDef;
  value: string;
  disabled: boolean;
  onSave: (val: string) => void;
}) => (
  <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md hover:bg-muted/30 transition-colors min-w-0 overflow-hidden">
    <CCTooltip label={field.label} description={field.description} serverOnly={field.serverOnly} className="flex-1">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-xs 2xl:text-sm text-foreground truncate block">{field.label}</span>
        {field.serverOnly && (
          <Badge variant={field.serverOnly === "emby" ? "success" : "muted"} size="xs" className="capitalize shrink-0">
            {field.serverOnly}
          </Badge>
        )}
      </div>
      <p className="text-[10px] 2xl:text-xs text-muted-foreground/60 mt-0.5 truncate block max-w-full">
        {field.description}
      </p>
    </CCTooltip>
    <Select
      size="compact"
      value={value}
      onChange={(e) => onSave(e.target.value)}
      disabled={disabled}
      aria-label={field.label}
      className="w-32 shrink-0"
    >
      {(field.enumOptions ?? []).map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </Select>
  </div>
);

/** Searchable tag picker with popover (Notion/Linear style) */
const TagsControl = ({
  field,
  value,
  disabled,
  onSave,
  suggestions,
}: {
  field: FieldDef;
  value: string[];
  disabled: boolean;
  onSave: (val: string[]) => void;
  suggestions?: string[];
}) => (
  <div className="py-1.5 px-2">
    <div className="flex items-center gap-1.5 mb-1.5">
      <span className="text-xs 2xl:text-sm text-foreground">{field.label}</span>
      {field.serverOnly && (
        <Badge variant={field.serverOnly === "emby" ? "success" : "muted"} size="xs" className="capitalize">
          {field.serverOnly}
        </Badge>
      )}
      <span className="text-[10px] 2xl:text-xs text-muted-foreground/50 ml-auto">
        {value.length} tag{value.length !== 1 ? "s" : ""}
      </span>
    </div>
    <TagPicker
      value={value}
      onChange={onSave}
      disabled={disabled}
      suggestions={suggestions}
      placeholder={`Select or create ${field.label.toLowerCase()}...`}
      aria-label={field.label}
      allowCreate
    />
    <div className="text-[10px] 2xl:text-xs text-muted-foreground/50 mt-0.5">
      {field.description}
    </div>
  </div>
);

/** Bypass Kill Stream — special WSD-local control with info tooltip */
const BypassKillStreamControl = ({
  value,
  loading,
  disabled,
  onToggle,
}: {
  value: boolean;
  loading: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}) => (
  <div className="flex items-center justify-between gap-2 py-1.5 px-2">
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full shrink-0 transition-colors",
          value ? "bg-emerald-500" : "bg-muted-foreground/25",
        )}
        aria-hidden="true"
      />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs 2xl:text-sm text-foreground">Bypass Kill Stream</span>
          <Tooltip
            maxWidth="xs"
            content={
              <div className="space-y-1.5 py-0.5">
                <p>When enabled, this user will not receive pause warnings or automatic stops.</p>
                <p className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400 font-medium">
                  <Icon icon="lucide:alert-triangle" className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>Exceptions: transcoding and Live TV sessions are always enforced.</span>
                </p>
              </div>
            }
          >
            <span tabIndex={0} className="cursor-help">
              <Icon icon="lucide:info" className="h-3 w-3 text-muted-foreground/60" />
            </span>
          </Tooltip>
        </div>
        <div className="text-[10px] 2xl:text-xs text-muted-foreground/60 mt-0.5">
          WSD-local setting, does not affect upstream server
        </div>
      </div>
    </div>
    {loading ? (
      <Spinner size="3" />
    ) : (
      <Switch checked={value} onChange={onToggle} disabled={disabled} aria-label="Toggle bypass kill stream" />
    )}
  </div>
);

// ============================================================================
// Category Card Component
// ============================================================================

const CategoryCard = ({
  category,
  profile,
  wsdPolicy,
  policyLoading,
  isBusy,
  serverType,
  halfWidth = false,
  librarySuggestions,
  onUpdateSettings,
  onUpdatePolicy,
  onRefreshProfile,
}: {
  category: FieldCategory;
  profile: UserProfile;
  wsdPolicy: UserPolicyData | null;
  policyLoading: boolean;
  isBusy: boolean;
  serverType: string;
  /** True when this card shares a row with another card (50% width) */
  halfWidth?: boolean;
  librarySuggestions: string[];
  onUpdateSettings: (patch: UserSettingsPatch) => Promise<void>;
  onUpdatePolicy: (update: Partial<UserPolicyData>) => Promise<void>;
  onRefreshProfile: () => void;
}) => {
  // Filter fields by server type
  const visibleFields = useMemo(
    () => category.fields.filter(
      (f) => !f.serverOnly || f.serverOnly === serverType,
    ),
    [category.fields, serverType],
  );

  if (visibleFields.length === 0) return null;

  // Split into compact (can share a grid row) vs wide (need full width)
  // Tag pickers (string[]) are wide; everything else is compact
  const compactFields = visibleFields.filter((f) => f.type !== "string[]");
  const wideFields = visibleFields.filter((f) => f.type === "string[]");
  const a = accentMap[category.accent];

  // Smart column count — context-aware mosaic approach
  // When halfWidth (paired card), max 2 cols since the card is ~50% viewport
  // When full width, up to 3 cols for dense categories
  const gridColClass = halfWidth
    ? (compactFields.length === 1
        ? "grid-cols-1"
        : "grid-cols-1 sm:grid-cols-2")
    : (compactFields.length === 1
        ? "grid-cols-1"
        : compactFields.length === 2
          ? "grid-cols-1 sm:grid-cols-2"
          : compactFields.length === 3
            ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
            : compactFields.length === 4
              ? "grid-cols-1 sm:grid-cols-2"
              : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3");

  // Generic save handler
  const handleSave = useCallback(async (field: FieldDef, value: unknown) => {
    if (!field.intentId) return;

    // Special case: WSD-local fields go through onUpdatePolicy
    if (field.bucket === "wsdLocal" && field.key === "bypassKillStream") {
      try {
        await onUpdatePolicy({ bypassKillStream: value as boolean });
        toast.success(value
          ? "Bypass enabled -- user will skip pause warnings and auto-stops"
          : "Bypass disabled -- user is subject to enforcement rules");
      } catch { toast.error("Failed to update bypass policy"); }
      return;
    }

    try {
      await onUpdateSettings({ [field.intentId]: value } as UserSettingsPatch);
      onRefreshProfile();
      if (field.type === "boolean") {
        toast.success(`${field.label} ${value ? "enabled" : "disabled"}`);
      } else {
        toast.success(`${field.label} updated`);
      }
    } catch {
      toast.error(`Failed to update ${field.label}`);
    }
  }, [onUpdateSettings, onUpdatePolicy, onRefreshProfile]);

  // Special handling for WSD category with bypassKillStream
  const isWsdCategory = category.id === "wsd";
  // Library category uses LibraryPickerPopover instead of tag pickers
  const isLibraryCategory = category.id === "library";

  return (
    <div>
      <SectionHeader icon={category.icon} accent={category.accent} count={visibleFields.length}>
        {category.title}
      </SectionHeader>
      <div
        className={cn(
          "rounded-lg border border-border/50 bg-card",
          "border-l-2",
          a.border,
        )}
      >
        {/* WSD category special treatment */}
        {isWsdCategory && visibleFields.some((f) => f.key === "bypassKillStream") && (
          <BypassKillStreamControl
            value={wsdPolicy?.bypassKillStream ?? false}
            loading={policyLoading}
            disabled={isBusy || policyLoading}
            onToggle={(v) => void handleSave(
              visibleFields.find((f) => f.key === "bypassKillStream")!,
              v,
            )}
          />
        )}

        {/* Compact fields (bool, number, enum, string) in a smart mosaic grid */}
        {!isWsdCategory && compactFields.length > 0 && (
          <div className={cn("grid gap-x-2 gap-y-0", gridColClass)}>
            {compactFields.map((field) => {
              const val = getFieldValue(field, profile, wsdPolicy);

              switch (field.type) {
                case "boolean":
                  return (
                    <ToggleTile
                      key={field.key}
                      field={field}
                      value={Boolean(val)}
                      disabled={isBusy}
                      onToggle={(v) => void handleSave(field, v)}
                    />
                  );
                case "number":
                  return (
                    <NumberControl
                      key={field.key}
                      field={field}
                      value={typeof val === "number" ? val : 0}
                      disabled={isBusy}
                      onSave={(v) => void handleSave(field, v)}
                    />
                  );
                case "string":
                  return (
                    <StringControl
                      key={field.key}
                      field={field}
                      value={typeof val === "string" ? val : ""}
                      disabled={isBusy}
                      onSave={(v) => void handleSave(field, v)}
                    />
                  );
                case "enum":
                  return (
                    <EnumControl
                      key={field.key}
                      field={field}
                      value={typeof val === "string" ? val : String(field.defaultValue)}
                      disabled={isBusy}
                      onSave={(v) => void handleSave(field, v)}
                    />
                  );
                default:
                  return null;
              }
            })}
          </div>
        )}

        {/* Library category: unified hierarchical picker */}
        {isLibraryCategory && (
          <div className={cn(compactFields.length > 0 && "border-t border-border/30", "py-1.5 px-2")}>
            <LibraryPickerPopover
              libraries={profile.availableLibraries}
              enabledFolderIds={profile.policy.enabledFolders}
              excludedSubFolderIds={profile.policy.excludedSubFolders}
              allFoldersEnabled={profile.policy.enableAllFolders}
              serverType={profile.serverType}
              disabled={isBusy}
              onLibraryChange={(ids) => {
                const idToName = new Map(
                  (profile.availableLibraries ?? []).map((l) => [normalizeId(l.id), l.name]),
                );
                const names = ids.map((id) => idToName.get(normalizeId(id)) ?? id);
                void handleSave(
                  { key: "EnabledFolders", intentId: "enabledFolders", type: "string[]", bucket: "policy", label: "Allowed Libraries", description: "", defaultValue: [] },
                  names,
                );
              }}
              onSubFolderChange={(ids) => void handleSave(
                { key: "ExcludedSubFolders", intentId: "excludedSubFolders", type: "string[]", bucket: "policy", label: "Excluded Sub-Folders", description: "", defaultValue: [] },
                ids,
              )}
            />
          </div>
        )}

        {/* Non-library wide fields (tag pickers) */}
        {!isWsdCategory && wideFields.length > 0 && (
          <div className={cn(compactFields.length > 0 && !isLibraryCategory && "border-t border-border/30")}>
            {wideFields.map((field) => {
              // Library category fields handled by LibraryPickerPopover above
              if (isLibraryCategory && (field.key === "EnabledFolderNames" || field.key === "ExcludedSubFolders")) return null;
              const val = getFieldValue(field, profile, wsdPolicy);
              return (
                <TagsControl
                  key={field.key}
                  field={field}
                  value={Array.isArray(val) ? val as string[] : []}
                  disabled={isBusy}
                  onSave={(v) => void handleSave(field, v)}
                  suggestions={field.key === "EnabledFolderNames" ? librarySuggestions : undefined}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

export const ControlsTab = ({
  profile,
  policy,
  policyLoading,
  settingsUpdating,
  policyUpdating,
  onUpdateSettings,
  onUpdatePolicy,
  onRefreshProfile,
}: ControlsTabProps) => {
  const isBusy = settingsUpdating || policyUpdating;

  // All available server libraries come from the profile response,
  // already resolved from VirtualFolders during profile assembly.
  const librarySuggestions = useMemo(
    () => (profile.availableLibraries ?? []).map((lib) => lib.name),
    [profile.availableLibraries],
  );

  // Filter categories that have visible fields for this server type
  const visibleCategories = useMemo(
    () => TEMPLATE_CATEGORIES.filter((cat) =>
      cat.fields.some(
        (f) => !f.serverOnly || f.serverOnly === profile.serverType,
      ),
    ),
    [profile.serverType],
  );

  // Group categories into a 2-column mosaic layout for visual variety
  // Larger categories (5+ fields) get full width, smaller ones share a row
  const layout = useMemo(() => {
    const items: Array<{ categories: FieldCategory[]; span: "full" | "half" }> = [];
    let pendingHalf: FieldCategory | null = null;

    for (const cat of visibleCategories) {
      const visibleCount = cat.fields.filter(
        (f) => !f.serverOnly || f.serverOnly === profile.serverType,
      ).length;

      if (visibleCount >= 5) {
        // Flush pending half
        if (pendingHalf) {
          items.push({ categories: [pendingHalf], span: "half" });
          pendingHalf = null;
        }
        items.push({ categories: [cat], span: "full" });
      } else {
        if (pendingHalf) {
          items.push({ categories: [pendingHalf, cat], span: "half" });
          pendingHalf = null;
        } else {
          pendingHalf = cat;
        }
      }
    }
    if (pendingHalf) {
      items.push({ categories: [pendingHalf], span: "half" });
    }

    return items;
  }, [visibleCategories, profile.serverType]);

  return (
    <div className="space-y-3">
      {layout.map((item, idx) => {
        if (item.span === "full") {
          return (
            <CategoryCard
              key={item.categories[0]!.id}
              category={item.categories[0]!}
              profile={profile}
              wsdPolicy={policy}
              policyLoading={policyLoading}
              isBusy={isBusy}
              serverType={profile.serverType}
              librarySuggestions={librarySuggestions}
              onUpdateSettings={onUpdateSettings}
              onUpdatePolicy={onUpdatePolicy}
              onRefreshProfile={onRefreshProfile}
            />
          );
        }

        return (
          <div key={`row-${idx}`} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {item.categories.map((cat) => (
              <CategoryCard
                key={cat.id}
                category={cat}
                profile={profile}
                wsdPolicy={policy}
                policyLoading={policyLoading}
                isBusy={isBusy}
                serverType={profile.serverType}
                halfWidth={item.categories.length > 1}
                librarySuggestions={librarySuggestions}
                onUpdateSettings={onUpdateSettings}
                onUpdatePolicy={onUpdatePolicy}
                onRefreshProfile={onRefreshProfile}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};
